import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ContractService } from './contract.service';


const CONTRACT_TYPES = [
  { value: 'Purchase Contract',    label: 'Purchase Contract'          },
  { value: 'Service Contract',     label: 'Service Contract'           },
  { value: 'NDA',                  label: 'Non-Disclosure Agreement'   },
  { value: 'Lease Agreement',      label: 'Lease Agreement'            },
  { value: 'Employment Contract',  label: 'Employment Contract'        },
  { value: 'Maintenance Agreement',label: 'Maintenance Agreement'      },
  { value: 'License Agreement',    label: 'License Agreement'          },
  { value: 'Other',               label: 'Other'                       },
];

const CURRENCIES = ['KES', 'USD', 'EUR', 'GBP', 'UGX', 'TZS'];
const PRIORITIES  = ['Low', 'Medium', 'High', 'Critical'];
const DEPARTMENTS = [
  'Information Technology', 'Finance', 'Human Resources',
  'Administration', 'Legal', 'Procurement', 'Marketing', 'Customer Service'
];

@Component({
  selector: 'app-executed-contract-upload',
  templateUrl: './executed-contract-upload.component.html',
  styleUrls: ['./executed-contract-upload.component.scss']
})
export class ExecutedContractUploadComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // ── Nav ────────────────────────────────────────────────────────────────────
  // ── Form model ─────────────────────────────────────────────────────────────
  form = {
    contractTitle:       '',
    contractType:        'Other',
    contractNumber:      '',        // blank = auto-generate on save
    supplierName:        '',
    companyName:         '',
    departmentName:      '',
    contractAmount:      null as number | null,
    currency:            'KES',
    signingDate:         '',
    effectiveDate:       '',
    expiryDate:          '',
    isRenewable:         false,
    autoRenew:           false,
    renewalTermMonths:   null as number | null,
    priority:            'Medium',
    confidential:        false,
    internalNotes:       '',
    // type-specific — kept flat, only active type's fields sent
    paymentTerms:        '',
    deliveryTerms:       '',
    warrantyPeriod:      null as number | null,
    serviceDescription:  '',
    slaTerms:            '',
    confidentialityPeriod: null as number | null,
    disclosingParty:     '',
    receivingParty:      '',
    propertyAddress:     '',
    leaseTermMonths:     null as number | null,
    securityDeposit:     null as number | null,
    jobTitle:            '',
    probationPeriod:     null as number | null,
    noticePeriod:        null as number | null,
    benefits:            '',
    maintenanceSchedule: '',
    responseTime:        null as number | null,
    coverageScope:       '',
    licenseType:         '',
    numberOfUsers:       null as number | null,
    territory:           '',
    restrictions:        '',
  };

  // ── Reminders ──────────────────────────────────────────────────────────────
  reminders: Array<{ purpose: string; reminderDate: string; }> = [];

  // ── File ───────────────────────────────────────────────────────────────────
  selectedFile: File | null = null;
  filePreviewName = '';
  fileSizeFormatted = '';
  isDragOver = false;

  // ── State ──────────────────────────────────────────────────────────────────
  isSaving       = false;
  saveError      = '';
  successMessage = '';

  // ── Lookup lists ───────────────────────────────────────────────────────────
  contractTypes = CONTRACT_TYPES;
  currencies    = CURRENCIES;
  priorities    = PRIORITIES;
  departments   = DEPARTMENTS;

  constructor(
    private router: Router,
    private contractService: ContractService
  ) {}

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Nav ────────────────────────────────────────────────────────────────────
  goBack(): void { this.router.navigate(['/contracts']); }

  // ── File handling ──────────────────────────────────────────────────────────
  triggerFilePicker(): void { this.fileInput.nativeElement.click(); }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.setFile(input.files[0]);
    }
  }

  onDragOver(e: DragEvent): void  { e.preventDefault(); this.isDragOver = true;  }
  onDragLeave(e: DragEvent): void { e.preventDefault(); this.isDragOver = false; }
  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.isDragOver = false;
    if (e.dataTransfer?.files[0]) this.setFile(e.dataTransfer.files[0]);
  }

  private setFile(file: File): void {
    this.selectedFile    = file;
    this.filePreviewName = file.name;
    this.fileSizeFormatted = this.formatFileSize(file.size);
  }

  removeFile(): void {
    this.selectedFile    = null;
    this.filePreviewName = '';
    this.fileSizeFormatted = '';
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024, sizes = ['B','KB','MB','GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
  }

  // ── Reminders ──────────────────────────────────────────────────────────────
  addReminder(): void {
    this.reminders.push({ purpose: 'Contract Expiry Warning', reminderDate: '' });
  }

  removeReminder(i: number): void { this.reminders.splice(i, 1); }

  generateDefaultReminders(): void {
    if (!this.form.expiryDate) return;
    this.reminders = [];
    const expiry = new Date(this.form.expiryDate);
    const today  = new Date();
    [90, 30, 7].forEach(days => {
      const rd = new Date(expiry);
      rd.setDate(rd.getDate() - days);
      if (rd > today) {
        this.reminders.push({
          purpose:      `Contract expiring in ${days} days`,
          reminderDate: rd.toISOString().split('T')[0]
        });
      }
    });
  }

  // ── Helpers: type-specific fields ─────────────────────────────────────────
  get showPurchaseFields():     boolean { return this.form.contractType === 'Purchase Contract'; }
  get showServiceFields():      boolean { return this.form.contractType === 'Service Contract'; }
  get showNdaFields():          boolean { return this.form.contractType === 'NDA'; }
  get showLeaseFields():        boolean { return this.form.contractType === 'Lease Agreement'; }
  get showEmploymentFields():   boolean { return this.form.contractType === 'Employment Contract'; }
  get showMaintenanceFields():  boolean { return this.form.contractType === 'Maintenance Agreement'; }
  get showLicenseFields():      boolean { return this.form.contractType === 'License Agreement'; }

  // ── Validation ────────────────────────────────────────────────────────────
  get isFormValid(): boolean {
    return !!(
      this.form.contractTitle.trim() &&
      this.form.supplierName.trim()  &&
      this.form.contractType          &&
      this.selectedFile
    );
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  submit(): void {
    if (!this.isFormValid || this.isSaving) return;

    this.isSaving       = true;
    this.saveError      = '';
    this.successMessage = '';

    const metadata: Record<string, any> = {
      contractTitle:     this.form.contractTitle,
      contractType:      this.form.contractType,
      contractNumber:    this.form.contractNumber    || undefined,
      supplierName:      this.form.supplierName,
      companyName:       this.form.companyName       || undefined,
      departmentName:    this.form.departmentName    || undefined,
      contractAmount:    this.form.contractAmount    || undefined,
      currency:          this.form.currency,
      signingDate:       this.form.signingDate       || undefined,
      effectiveDate:     this.form.effectiveDate     || undefined,
      expiryDate:        this.form.expiryDate        || undefined,
      isRenewable:       this.form.isRenewable,
      autoRenew:         this.form.autoRenew,
      renewalTermMonths: this.form.renewalTermMonths || undefined,
      priority:          this.form.priority,
      confidential:      this.form.confidential,
      internalNotes:     this.form.internalNotes     || undefined,
      reminders: this.reminders.filter(r => r.reminderDate).map((r, i) => ({
        id: i + 1,
        purpose: r.purpose,
        reminderDate: r.reminderDate,
        sent: false
      })),
      // Type-specific fields (only add if relevant)
      ...(this.showPurchaseFields    ? { paymentTerms: this.form.paymentTerms, deliveryTerms: this.form.deliveryTerms, warrantyPeriod: this.form.warrantyPeriod } : {}),
      ...(this.showServiceFields     ? { serviceDescription: this.form.serviceDescription, slaTerms: this.form.slaTerms, paymentTerms: this.form.paymentTerms } : {}),
      ...(this.showNdaFields         ? { confidentialityPeriod: this.form.confidentialityPeriod, disclosingParty: this.form.disclosingParty, receivingParty: this.form.receivingParty } : {}),
      ...(this.showLeaseFields       ? { propertyAddress: this.form.propertyAddress, leaseTermMonths: this.form.leaseTermMonths, securityDeposit: this.form.securityDeposit } : {}),
      ...(this.showEmploymentFields  ? { jobTitle: this.form.jobTitle, probationPeriod: this.form.probationPeriod, noticePeriod: this.form.noticePeriod, benefits: this.form.benefits } : {}),
      ...(this.showMaintenanceFields ? { maintenanceSchedule: this.form.maintenanceSchedule, responseTime: this.form.responseTime, coverageScope: this.form.coverageScope } : {}),
      ...(this.showLicenseFields     ? { licenseType: this.form.licenseType, numberOfUsers: this.form.numberOfUsers, territory: this.form.territory, restrictions: this.form.restrictions } : {}),
    };

    this.contractService
      .uploadExecutedContract(this.selectedFile!, metadata)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isSaving = false;
          if (result.success) {
            this.successMessage = `Contract "${result.contractNumber}" uploaded successfully.`;
            setTimeout(() => this.router.navigate(['/contract-details', result.nodeId]), 1200);
          } else {
            this.saveError = result.error || 'Upload failed.';
          }
        },
        error: (err) => {
          this.isSaving  = false;
          this.saveError = err.message || 'Upload failed. Please try again.';
        }
      });
  }
}
